export type StatusObject = {
  status: boolean
  message: string
  data?: any
}

export type User = {
  email: string
  lastName: string
  firstName: string
  status: string
  dob: string
  nationality: string
  sex: string
  account_id: string
  dateCreated: string
}

export type JWTDecodedModel = {
  email: string
  lastName: string
  firstName: string
  status: string
  dob: string
  dateCreated: string
  nationality: string
  sex: string
  account_id: string
  ica: number
  exp: number
}

import axios, { AxiosError } from 'axios'
import { decode } from './encryption'
import localStore from './storage'
import { configuration as config } from './utils'
import axiosAuth from './httpAuth'
import dayjs from 'dayjs'

// Default status message for errors, if no message is provided.
const defaultMessage = 'Unable to process request.'

// Auth API Base Endpoint
const AUTH_URI = config.BASE_API_URL + '/auth'

// Appends Auth API Base Endpoint to the parameter passed, generates new endpoint
const $ = (endpoint: string) => AUTH_URI + endpoint

/**
 * @desc returns the JWT stored in local storage
 * @returns {object}
 */
const getToken = () => localStore.get(config.AUTH_STORE_KEY, true)

export type RegisterFormModel = {
  email: string
  password: string
  confirmPassword: string
  lastName: string
  firstName: string
  sex: string
  dob: Date | string
  nationality: string
}

export type RegisterResponseObject = StatusObject & {
  user: User
}

/**
 * Used to register new user accounts, will create a POST request to the /auth/register endpoint
 * @param user
 * @returns
 */
const register = async (user: RegisterFormModel): Promise<RegisterResponseObject | StatusObject> => {
  try {
    return await axios
      .post($('/register'), { ...user, dateOfBirth: dayjs(user.dob).format('YYYY-MM-DD') })
      .then((response) => response.data)
  } catch (error) {
    console.log(error)
    return {
      status: false,
      message: error instanceof AxiosError ? error.message : defaultMessage,
    }
  }
}
/**
 * Will create a POST request to the /auth/confirm-code endpoint in order to confirm and verify a verification code.
 * @param code
 * @returns
 */
const confirmCode = async (code: string): Promise<StatusObject> => {
  try {
    return await axios.post($('/confirm-code'), { code }).then((response) => response.data)
  } catch (error) {
    console.log(error)
    return {
      status: false,
      message: !(error instanceof AxiosError) ? defaultMessage : error.response?.data?.message ?? error.message,
    }
  }
}
/**
 * Will resend a code to the provided email by creating a POST request to the /auth/resend-code endpoint
 * @param email
 * @returns
 */
const resendCode = async (email: string): Promise<StatusObject> => {
  try {
    return await axios.post($('/resend-code'), { email }).then((response) => response.data)
  } catch (error) {
    return {
      status: false,
      message: !(error instanceof AxiosError) ? defaultMessage : error.response?.data?.message ?? error.message,
    }
  }
}
/**
 * Will update the local storage user info, this is for updating only the [F]ront [E]nd user info.
 * @param user
 * @returns
 */
const updateUserInfoFE = (user: User) => {
  const _user = getUser(false)

  if (!_user || !user) return

  const replacement_user = {
    email: user.email ?? _user.email,
    lastName: user.lastName ?? _user.lastName,
    firstName: user.firstName ?? _user.firstName,
    status: user.status ?? _user.status,
    dob: user.dob ?? user.dob ?? _user.dob,
    nationality: user.nationality ?? _user.nationality,
    sex: user.sex ?? _user.sex,
    account_id: user.account_id ?? _user.account_id,
    dateCreated: user.dateCreated ?? _user.dateCreated,
  }

  localStore.set(config.AUTH_USER_INFO, replacement_user, true)
  return replacement_user
}

/**
 * extracts the Payload part of the JWT
 * @returns
 */
const _getJWTDecoded = (): JWTDecodedModel | null => {
  let jwt = localStore.get(config.AUTH_STORE_KEY, true)

  if (!jwt) return null

  jwt = jwt.split('.')[1]

  return JSON.parse(decode(jwt))
}

/**
 * Will return the User Info found in local storage
 * @returns
 */
const _getUserInfoDecoded = (): User | null => {
  const userInfo = localStore.get(config.AUTH_USER_INFO, true)
  if (!userInfo) return null

  return userInfo as unknown as User
}

/**
 * checks to see if a JWT that is not expired exists in Local Storage.
 */
const isLoggedIn = () => {
  const user = _getJWTDecoded()

  if (user) {
    const jwtExp = user.exp

    if (Date.now() >= jwtExp * 1000) {
      localStore.remove(config.AUTH_STORE_KEY)
      localStore.remove(config.AUTH_USER_INFO)

      return false
    } else return true
  } else return false
}

/**
 * Will get the user info either from the JWT payload or from the User Info Local Storage entry
 * @param fromJWT
 * @returns
 */
const getUser = (fromJWT = true): User | JWTDecodedModel | null => (fromJWT ? _getJWTDecoded() : _getUserInfoDecoded())

const login = async (email: string, password: string): Promise<StatusObject> => {
  try {
    // make sure email and password were provided.
    if (!email || !password) throw new Error('User email and password is required.')
    // make request to service to login
    const result = await axios.post($('/login'), { email, password }).then((response) => response.data)
    // if no status is provided then something is wrong.
    if (!(result.status ?? false)) throw new Error(result.message)
    // if no JWT exists then something is wrong
    if (!result.jwt) throw new Error(result.message ?? 'Unable to login user.')

    localStore.set(config.AUTH_STORE_KEY, result.jwt, true) // save the JWT in local storage
    let jwt = result.jwt.split('.')[1] // extract the user info from the JWT

    localStore.set(config.AUTH_USER_INFO, jwt, false)
    jwt = decode(jwt)

    return {
      status: true,
      message: result.message,
      data: { user: jwt },
    }
  } catch (error) {
    return {
      status: false,
      message: !(error instanceof AxiosError) ? defaultMessage : error.response?.data?.message ?? error.message,
    }
  }
}

/**
 * Will remove and expire the JWT and user information from local storage. Will redirect the user, to the login page.
 */
const logout = () => {
  localStore.remove(config.AUTH_STORE_KEY)
  localStore.remove(config.AUTH_USER_INFO)
}

/**
 * Will generate a POST
 * @param payload
 * @returns
 */
const resetPassword = async (payload: any): Promise<StatusObject> => {
  try {
    return await axios.post($('/reset-password'), payload).then((response) => response.data)
  } catch (error) {
    console.log(error)
    return {
      status: false,
      message: !(error instanceof AxiosError) ? defaultMessage : error.response?.data?.message ?? error.message,
    }
  }
} /**
 * creates a POST request to /auth/accounts to update the user's email, user most be logged in
 * @param email
 * @returns
 */
const updateEmail = async (email: string): Promise<StatusObject> => {
  try {
    return await axiosAuth.post('/auth/accounts', { item: 'email', email }).then((response) => response.data)
  } catch (error) {
    return {
      status: false,
      message: !(error instanceof AxiosError) ? defaultMessage : error.response?.data?.message ?? error.message,
    }
  }
}

/**
 * Creates a POST request to /auth/accounts to update user information.
 * @param user
 * @returns
 */
const updateUserInfo = async (user: User) => {
  try {
    return await axiosAuth.post('/auth/accounts', { item: 'user info', user }).then((response) => response.data)
  } catch (error) {
    return {
      status: false,
      message: !(error instanceof AxiosError) ? defaultMessage : error.response?.data?.message ?? error.message,
    }
  }
}

export {
  getToken,
  register,
  confirmCode,
  resendCode,
  updateUserInfoFE,
  isLoggedIn,
  getUser,
  login,
  logout,
  resetPassword,
  updateEmail,
  updateUserInfo,
}
